<template>
  <div class="view-container reference-users-page">
    <div class="extended-filter-block">
      <h2>Поиск респондентов</h2>

      <div class="extended-search-block-mobile">
        <div class="search-outline-block">
          <SearchOutlined
              @click="showMobileFilterModal = true"
              style="color: rgba(0, 0, 0, 0.45)"
          />

          <div v-if="currentFiltersLine" class="dot"></div>
        </div>
      </div>
    </div>

    <div class="card card__with-action">
      <div class="reference-users-page__top">
        <div class="reference-users-page__tabs">
          <a-segmented
              v-model:value="currentLevel"
              :options="levelOptions"
          />
        </div>

        <div class="action-button-wrapper">
          <a-button @click="backToReport" class="secondary-button">
            К отчету
          </a-button>

          <a-button class="secondary-button" :loading="excelGenerating" @click="requestExcelReport">
            <template #icon v-if="!excelGenerating">
              <DownloadOutlined />
            </template>
            Скачать
          </a-button>
        </div>

        <div class="group-action-wrapper">
          <a-dropdown :trigger="['click']" placement="bottomLeft">
            <a-button type="text" @click.prevent>
              <MoreOutlined class="more-button" />
            </a-button>

            <template #overlay>
              <a-menu>
                <a-menu-item @click="backToReport" key="back">К отчету</a-menu-item>
                <a-menu-item @click="requestExcelReport" key="excel">Скачать</a-menu-item>
              </a-menu>
            </template>
          </a-dropdown>
        </div>
      </div>

      <TableHorizontalScrollShell ref="tableShell">
        <a-table
            ref="tableRef"
            rowKey="user_id"
            :columns="columns"
            :data-source="userData"
            :scroll="tableScroll"
            :row-selection="{ selectedRowKeys: selectedUsers, onChange: onSelectChange, columnWidth: 40 }"
            :pagination="false"
            :loading="tableLoading"
            :table-layout="'fixed'"
            :row-class-name="getRowClassName"
            @change="handleTableChange"
        >
          <template #bodyCell="{ column, text, record }">
            <template v-if="column.dataIndex === 'name'">
              <a
                  class="staff-link"
                  @click="router.push(`/staff/${record.user_id}/edit`)"
              >
                {{ text }}
              </a>
            </template>

            <template v-if="column.dataIndex === 'results'">
              <div class="flex-vertical-align">
                <span
                    class="green-text"
                    @click="handleAction('show-reports', record)"
                >
                  Отчеты
                </span>

                <a-divider type="vertical" />

                <span
                    class="green-text"
                    @click="handleAction('show-courses', record)"
                >
                  История обучения
                </span>
              </div>
            </template>

            <template v-if="column.dataIndex === 'groups'">
              <a-tag
                  v-for="group in record.groups"
                  :key="group.id"
                  class="group-tag"
              >
                {{ group.name }}
              </a-tag>
            </template>

            <template v-if="column.dataIndex === 'settings'">
              <a-dropdown :trigger="['click']" placement="bottomLeft">
                <a-button type="text" @click.prevent>
                  <MenuOutlined />
                </a-button>

                <template #overlay>
                  <a-menu @click="({ key }) => handleAction(key, record)">
                    <a-menu-item key="groups">
                      Настроить группы
                    </a-menu-item>
                  </a-menu>
                </template>
              </a-dropdown>
            </template>
          </template>

          <template #emptyText>
            <div class="no-data-wrapper">
              <img
                  :src="NotFoundIcon"
                  class="no-data-image"
                  alt="Нет результатов"
              />

              <div class="no-data-text">
                {{
                  currentFiltersLine || currentLevel !== 'all'
                      ? 'Нет данных для отображения. Измените фильтры или вкладку.'
                      : 'Нет данных для отображения.'
                }}
              </div>

              <a-button
                  v-if="currentFiltersLine || currentLevel !== 'all'"
                  @click="resetFilters"
              >
                <template #icon>
                  <ReloadOutlined />
                </template>

                Сбросить фильтры
              </a-button>
            </div>
          </template>
        </a-table>
      </TableHorizontalScrollShell>

      <div class="bottom-container">
        <div class="action-block reference-users-page__actions" v-if="!isMobile || isMobile && selectedUsers.length">
          <div class="action-select-wrapper">
            <a-select
                v-model:value="actionToApply"
                placeholder="Выберите действие"
                style="width: 100%;"
                :not-found-content="'Нет данных'"
                :disabled="!selectedUsers.length && !applyToAll"
                @change="() => handleAction(actionToApply)"
            >
              <a-select-option
                  v-for="action in actions"
                  :key="action.id"
                  :value="action.id"
              >
                {{ action.name }}
              </a-select-option>
            </a-select>
          </div>
        </div>

        <a-pagination
            v-model:current="currentPage"
            :page-size="entitiesPerPage"
            :buildOptionText="count => `${count.value} / стр`"
            :show-total="isMobile ? undefined : (total => `Всего ${total ?? 0} элементов`)"
            :total="totalUsers"
            :show-less-items="isMobile"
            @change="handlePageChange"
        />
      </div>
    </div>

    <MobileReferenceProfileUserFilterForm
        v-if="showMobileFilterModal"
        :open="showMobileFilterModal"
        :groups="groups"
        @close="showMobileFilterModal = false"
    />

    <SyncGroupsForm
        v-if="currentAction === 'groups'"
        :open="currentAction === 'groups'"
        :group-list="groups"
        :user_groups="currentActionUser.groups"
        :user_id="currentActionUser.id"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <CopyUsersIntoGroup
        v-if="currentAction === 'copy_to_group'"
        :open="currentAction === 'copy_to_group'"
        :group-list="groups"
        :users="actionUserIds"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <SendInviteForm
        v-if="currentAction === 'send_invite'"
        :open="currentAction === 'send_invite'"
        :currentActionUser="currentActionUser"
        :selectedUsers="actionUserIds"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <BlockUserForm
        v-if="currentAction === 'block' || currentAction === 'unblock'"
        :open="currentAction === 'block' || currentAction === 'unblock'"
        :type="currentAction"
        :currentActionUser="currentActionUser"
        :selectedUsers="actionUserIds"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <ResetPasswordForm
        v-if="currentAction === 'reset_password'"
        :open="currentAction === 'reset_password'"
        :currentActionUser="currentActionUser"
        :selectedUsers="actionUserIds"
        @submitted="fetchAfterSubmit"
        @close="clearCurrentUserAndAction"
    />

    <ManageAccessForm
        v-if="currentAction === 'manage_access'"
        :open="currentAction === 'manage_access'"
        :userData="currentActionUser?.id ? [currentActionUser.id] : actionUserIds"
        :name="currentActionUser?.id ? currentActionUser.name : null"
        :mode="'staff'"
        @close="clearCurrentUserAndAction"
        @submitted="fetchAfterSubmit"
        @showStaff="handleShowStaff"
        @showOrderForm="handleShowOrderForm"
    />

    <PassingReportTypes
        v-if="currentAction === 'show-reports'"
        :open="currentAction === 'show-reports'"
        :passings="currentPassing"
        @close="clearCurrentUserAndAction"
    />

    <DetailsPassings
        v-if="currentAction === 'show-courses'"
        :open="currentAction === 'show-courses'"
        :courses="coursesToShow"
        :email="currentActionUser.email"
        @close="clearCurrentUserAndAction"
    />

    <NewOrderForm
        v-if="showOrderForm"
        :open="showOrderForm"
        :initial-open-tables="['analytics']"
        @close="showOrderForm = false"
    />
  </div>
</template>

<script>
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {onBeforeRouteLeave, useRoute, useRouter} from 'vue-router'
import { message } from 'ant-design-vue'
import {
  SearchOutlined,
  ReloadOutlined,
  MenuOutlined, DownloadOutlined, MoreOutlined,
} from '@ant-design/icons-vue'

import NotFoundIcon from '@/assets/images/icons/no_data.svg'
import UsersFilterMobileForm from '@/feature/users/UsersFilterMobileForm.vue'
import ManageAccessForm from '@/components/Forms/Access/ManageAccessForm.vue'
import NewOrderForm from '@/views/Orders/OrderForm.vue'
import ResetPasswordForm from '@/feature/users/forms/ResetPasswordForm.vue'
import BlockUserForm from '@/feature/users/forms/BlockUserForm.vue'
import SendInviteForm from '@/feature/users/forms/SendInviteForm.vue'
import CopyUsersIntoGroup from '@/feature/group-control/forms/CopyUsersIntoGroup.vue'
import SyncGroupsForm from '@/feature/group-control/forms/SyncGroupsForm.vue'
import DetailsPassings from '@/views/Products/DetailsPassings.vue'
import PassingReportTypes from '@/components/Cards/PassingReportTypes.vue'
import TableHorizontalScrollShell from '@/components/Tables/TableScrollComponent.vue'
import { useTableColumns } from '@/mixins/useTableColumns'
import MobileReferenceProfileUserFilterForm from "@/feature/users/MobileReferenceProfileUserFilterForm.vue";

export default {
  name: 'reference-users-report',

  components: {
    MoreOutlined,
    DownloadOutlined,
    MobileReferenceProfileUserFilterForm,
    TableHorizontalScrollShell,
    UsersFilterMobileForm,
    ManageAccessForm,
    NewOrderForm,
    ResetPasswordForm,
    BlockUserForm,
    SendInviteForm,
    CopyUsersIntoGroup,
    SyncGroupsForm,
    DetailsPassings,
    PassingReportTypes,
    SearchOutlined,
    ReloadOutlined,
    MenuOutlined,
  },

  setup() {
    const store = useStore();
    const router = useRouter();

    const tableShell = ref(null);
    const tableLoading = ref(true);
    const groupsLoading = ref(true);

    const showMobileFilterModal = ref(false);
    const showOrderForm = ref(false);

    const currentLevel = ref('all');
    const selectedUsers = ref([]);
    const applyToAll = ref(false);
    const actionToApply = ref(null);
    const currentAction = ref(null);

    const currentPage = ref(1);
    const entitiesPerPage = ref(10);

    const coursesToShow = ref([]);
    const currentPassing = ref([]);

    const route = useRoute();

    const currentActionUser = ref({
      id: null,
      name: '',
      email: '',
      groups: [],
    });

    const allGroups = computed(() => store.state.group.groups);
    const groups = ref([]);

    const filters = computed(() => store.state.users.filters);
    const currentFiltersLine = computed(() => store.state.users.filterString);

    const reportData = computed(() => store.state.analytics.referenceProfileUsers?.data ?? {});

    const totalUsers = computed(() => {
      return store.state.analytics.referenceProfileUsers?.total ?? 0;
    });

    const levelOptions = [
      {
        label: 'Все',
        value: 'all',
      },
      {
        label: 'Высокий',
        value: 'high',
      },
      {
        label: 'Средний',
        value: 'middle',
      },
      {
        label: 'Низкий',
        value: 'low',
      },
    ]

    const typeToLevelMap = {
      accord: 'high',
      accord_range: 'middle',
      not_accord: 'low',
    }

    const levelToTypeMap = {
      high: 'accord',
      middle: 'accord_range',
      low: 'not_accord',
    }

    const initialColumns = ref([
      {
        title: 'Сотрудник',
        dataIndex: 'name',
        sorter: true,
        visible: true,
        width: 260,
      },
      {
        title: 'Совпадение',
        dataIndex: 'aligned',
        sorter: true,
        visible: true,
        width: 100,
      },
      {
        title: 'Общий ЭИ',
        dataIndex: 'total',
        sorter: true,
        visible: true,
        width: 105,
      },
      {
        title: 'Опытный домен',
        dataIndex: 'domain_1',
        sorter: true,
        visible: true,
        width: 170,
      },
      {
        title: 'Стратегический домен',
        dataIndex: 'domain_2',
        sorter: true,
        visible: true,
        width: 200,
      },
      {
        title: 'Распознавание',
        dataIndex: 'branch_1',
        sorter: true,
        visible: true,
        width: 145,
      },
      {
        title: 'Использование',
        dataIndex: 'branch_2',
        sorter: true,
        visible: true,
        width: 145,
      },
      {
        title: 'Анализ',
        dataIndex: 'branch_3',
        sorter: true,
        visible: true,
        width: 100,
      },
      {
        title: 'Управление',
        dataIndex: 'branch_4',
        sorter: true,
        visible: true,
        width: 125,
      },
      {
        title: 'Лица',
        dataIndex: 'section_1',
        sorter: true,
        visible: true,
        width: 90,
      },
      {
        title: 'Среда',
        dataIndex: 'section_5',
        sorter: true,
        visible: true,
        width: 90,
      },
      {
        title: 'Ситуации',
        dataIndex: 'section_9a',
        sorter: true,
        visible: true,
        width: 105,
      },
      {
        title: 'Фасилитация',
        dataIndex: 'section_2',
        sorter: true,
        visible: true,
        width: 120,
      },
      {
        title: 'Эмпатия',
        dataIndex: 'section_6',
        sorter: true,
        visible: true,
        width: 100,
      },
      {
        title: 'Изменения',
        dataIndex: 'section_3',
        sorter: true,
        visible: true,
        width: 110,
      },
      {
        title: 'Соединения',
        dataIndex: 'section_7',
        sorter: true,
        visible: true,
        width: 120,
      },
      {
        title: 'Динамика',
        dataIndex: 'section_9b',
        sorter: true,
        visible: true,
        width: 105,
      },
      {
        title: 'Саморегуляция',
        dataIndex: 'section_4',
        sorter: true,
        visible: true,
        width: 145,
      },
      {
        title: 'Влияние',
        dataIndex: 'section_8',
        sorter: true,
        visible: true,
        width: 100,
      },
      {
        title: 'Результаты',
        dataIndex: 'results',
        sorter: false,
        width: 200,
      },
      {
        title: 'Группы',
        dataIndex: 'groups',
        sorter: false,
        visible: true,
        width: 250,
      },
      {
        title: 'Должность',
        dataIndex: 'job_title',
        sorter: true,
        visible: true,
        width: 180,
      },
    ])

    const { columns, scrollX, scrollY, allColumns, tableRef, tableScroll, updateTableYScroll } = useTableColumns(
        initialColumns,
        {
          tableKey: 'reference_users_report',
          settingsWidth: 40,
          selectionWidth: 40,
        },
    )

    const actions = [
      {
        id: 'copy_to_group',
        name: 'Объединить в группу',
      },
      {
        id: 'reset_password',
        name: 'Сбросить пароль',
      },
      {
        id: 'manage_access',
        name: 'Управлять доступами',
      },
      {
        id: 'send_invite',
        name: 'Отправить приглашения',
      },
      {
        id: 'block',
        name: 'Архивировать',
      },
    ]

    const sortState = ref({
      field: null,
      order: null,
    });

    const requestParams = computed(() => {
      return {
        per_page: entitiesPerPage.value,
        page: currentPage.value,
        sort_field: sortState.value.field,
        sort_direction:
            sortState.value.order === 'ascend'
                ? 'asc'
                : sortState.value.order === 'descend'
                    ? 'desc'
                    : null,
        level: currentLevel.value === 'all' ? null : currentLevel.value,
        type: currentLevel.value === 'all' ? null : levelToTypeMap[currentLevel.value],
        ...filters.value,
      }
    })

    const userData = computed(() => {
      return (reportData.value ?? []).map((user) => {
        const type = user.type ?? levelToTypeMap[user.level] ?? null

        return {
          ...user,
          type,
          aligned: user.aligned ? (user.aligned + '%') : 0,
          level: user.level ?? typeToLevelMap[type] ?? null,
          status: user.status ?? (user.is_active === false ? 'not_active' : 'active'),
          groups: user.groups ?? [],
          tests: user.tests ?? user.passings ?? [],
          courses: user.courses ?? [],
          job_title: user.job_title ?? user.position ?? user.profession ?? '',
        }
      })
    })

    const actionUserIds = computed(() => {
      if (currentActionUser.value?.id) {
        return [currentActionUser.value.id]
      }

      return selectedUsers.value
    })

    const fetchUsers = () => {
      tableLoading.value = true

      return store.dispatch("analytics/getReferenceReportUsers",
          {id: route.params.id, ...requestParams.value}
      )
          .finally(() => {
            tableLoading.value = false
          })
    }

    const getRowClassName = (record) => {
      return record.type ? `user-type-${record.type}` : ''
    }

    const onSelectChange = (selectedRowKeys) => {
      selectedUsers.value = selectedRowKeys
    }

    const resetFilters = () => {
      currentLevel.value = 'all'
      currentPage.value = 1
      selectedUsers.value = []
      applyToAll.value = false

      store.dispatch('users/setUserFilters', {
        filters: {
          name: '',
          job_title: '',
          department: '',
          online_from: '',
          created_at: '',
          email: '',
          telephone: '',
          groups: [],
          without_group_only: false,
          level_from: null,
          level_to: null,
        },
        filterString: null,
      })

      fetchUsers()
    }

    const clearCurrentUserAndAction = () => {
      currentAction.value = null
      actionToApply.value = null
      currentPassing.value = []
      coursesToShow.value = []

      currentActionUser.value = {
        id: null,
        name: '',
        email: '',
        groups: [],
      }
    }

    const fetchAfterSubmit = () => {
      clearCurrentUserAndAction()
      selectedUsers.value = []
      applyToAll.value = false
      fetchUsers()
    }

    const handleShowStaff = () => {
      selectedUsers.value = []
      applyToAll.value = false
      clearCurrentUserAndAction()
      fetchUsers()
    }

    const handleShowOrderForm = () => {
      clearCurrentUserAndAction()
      showOrderForm.value = true
    }

    const handleAction = (action, userObject = null) => {
      if (!action) {
        return
      }

      if (userObject) {
        currentActionUser.value = {
          id: userObject.user_id,
          name: userObject.name,
          email: userObject.email ?? '',
          groups: (userObject.groups ?? []).map((group) => {
            if (!groups.value.find((el) => el.id === group.id)) {
              groups.value.push({
                id: group.id,
                name: group.name,
              })
            }

            return group.id
          }),
        }
      } else {
        currentActionUser.value = {
          id: null,
          name: '',
          email: '',
          groups: [],
        }
      }

      if (action === 'edit') {
        router.push(`/staff/${userObject.user_id}/edit`)
        return
      }

      if (action === 'show-reports') {
        currentPassing.value = userObject.tests ?? []
        currentAction.value = action
        return
      }

      if (action === 'show-courses') {
        coursesToShow.value = userObject.courses ?? []
        currentAction.value = action
        return
      }

      if (!currentActionUser.value?.id && !selectedUsers.value.length && !applyToAll.value) {
        message.error('Выберите сотрудников')
        actionToApply.value = null
        return
      }

      currentAction.value = action
    }

    let excelGenerating = ref(false);

    const requestExcelReport = () => {
      excelGenerating.value = true;
      store.dispatch('analytics/getReferenceProfileReport', route.params.id)
        .finally(() => {
          excelGenerating.value = false;
        })

      message.info('Файл генерируется, пожалуйста, подождите')
    }

    const handleTableChange = (pagination, filter, sorter) => {
      sortState.value = {
        field: sorter?.field ?? null,
        order: sorter?.order ?? null,
      }

      currentPage.value = 1
      selectedUsers.value = []
      applyToAll.value = false

      fetchUsers()
    }

    const retrieveEntitiesPerPage = () => {
      const storageValue = localStorage.getItem('perPage')
      entitiesPerPage.value = storageValue ? Number(storageValue) : 10
    }

    const setEntitiesPerPage = (num) => {
      localStorage.setItem('perPage', num)
    }

    const handlePageChange = (current, perPage) => {
      if (entitiesPerPage.value !== perPage) {
        entitiesPerPage.value = perPage
        setEntitiesPerPage(entitiesPerPage.value)
      }

      currentPage.value = current
      selectedUsers.value = []
      applyToAll.value = false

      fetchUsers()
    }

    const listState = computed(() => store.state.users.staffListState);

    onBeforeMount(() => {
      retrieveEntitiesPerPage();

      currentPage.value = listState.value.currentPage ?? 1
      sortState.value = {
        field: listState.value.sortField ?? null,
        order: listState.value.sortOrder ?? null,
      }

      currentLevel.value = listState.value.currentLevel ?? 'all';

      store.dispatch('group/getGroups').then(() => {
        groups.value = allGroups.value.map((group) => {
          return {
            id: group.id,
            name: group.name,
          }
        })

        groupsLoading.value = false
      })

      fetchUsers()
    });

    const backToReport = () => {
      router.push({ path:'/profiles/report/', query: { id: route.params.id }});
    }

    watch(currentLevel, () => {
      currentPage.value = 1
      selectedUsers.value = []
      applyToAll.value = false

      store.commit('users/setStaffListState', {
        currentPage: currentPage.value,
        sortField: sortState.value.field,
        sortOrder: sortState.value.order,
        currentLevel: currentLevel.value,
      });

      fetchUsers();
    }, {deep: true})

    watch(
        [currentPage, entitiesPerPage, sortState],
        () => {
          store.commit('users/setStaffListState', {
            currentPage: currentPage.value,
            sortField: sortState.value.field,
            sortOrder: sortState.value.order,
            currentLevel: currentLevel.value,
          })
        },
        { deep: true }
    )

    watch(filters, () => {
      currentPage.value = 1
      selectedUsers.value = []
      applyToAll.value = false
      showMobileFilterModal.value = false
      fetchUsers();
    }, { deep: true })

    watch(tableLoading, async (loading) => {
      if (!loading) {
        await tableShell.value?.rebindScroll()
        await updateTableYScroll()
      }
    })

    watch(userData, async () => {
      await nextTick()
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, { deep: true })

    watch(allColumns, async () => {
      await nextTick()
      await tableShell.value?.rebindScroll()
      await updateTableYScroll()
    }, { deep: true })

    onBeforeRouteLeave((to) => {
      const isStillInStaff = to.path.startsWith('/staff')

      if (!isStillInStaff) {
        store.commit('users/clearStaffListState')
        store.commit('analytics/clearReferenceProfileHeader')
        store.dispatch('users/clearUserFilters')
      }
    });

    return {
      router,
      NotFoundIcon,

      tableShell,
      tableRef,
      tableScroll,
      tableLoading,
      groupsLoading,
      showMobileFilterModal,
      showOrderForm,

      currentLevel,
      levelOptions,
      currentFiltersLine,

      columns,
      scrollX,
      scrollY,

      userData,
      totalUsers,

      selectedUsers,
      applyToAll,
      actionToApply,
      actions,
      actionUserIds,

      groups,
      currentAction,
      currentActionUser,
      currentPassing,
      coursesToShow,

      currentPage,
      entitiesPerPage,

      onSelectChange,
      handleAction,
      handleTableChange,
      handlePageChange,
      getRowClassName,
      resetFilters,
      clearCurrentUserAndAction,
      fetchAfterSubmit,
      handleShowStaff,
      handleShowOrderForm,
      fetchUsers,
      backToReport,
      requestExcelReport,
      excelGenerating
    }
  },
}
</script>

<style scoped lang="scss">
.reference-users-page {
  padding-top: 0;
  padding-bottom: 0;
}

.card__with-action {
  margin-bottom: 0;
}

.reference-users-page__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;

  @media (max-width: $mobile-breakpoint) {
      gap: 8px;
  }
}

.reference-users-page__title-block {
  min-width: 0;
}

.reference-users-page__title {
  margin-bottom: 12px;
  font-weight: 600;
  font-size: 16px;
  line-height: 120%;
}

.reference-users-page__legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.reference-users-page__legend-item {
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.2;
  color: var(--main-font);
}

.reference-users-page__legend-item--accord {
  background: #beffe0;
}

.reference-users-page__legend-item--accord-range {
  background: #fff1c5;
}

.reference-users-page__legend-item--not-accord {
  background: #ffd2d4;
}

.reference-users-page__tabs {
  flex-shrink: 0;
}

.reference-users-page__actions {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.action-select-wrapper {
  min-width: 180px;

  @media (max-width: $mobile-breakpoint) {
    width: 100%;

    .ant-select {
      width: 100%;
    }
  }
}

.extended-filter-block {
  display: none;

  @media (max-width: $mobile-breakpoint) {
    margin: 24px 20px;
    display: flex;
    column-gap: 25px;
    align-items: center;
    justify-content: space-between;
  }
}

.extended-filter-block h2 {
  font-size: 20px;
  margin-bottom: 0;
  font-weight: 600;
  line-height: 120%;
}

.extended-search-block-mobile {
  display: none;
  margin-right: 10px;

  @media (max-width: 768px) {
    display: block;
  }
}

.search-outline-block {
  position: relative;
}

.dot {
  position: absolute;
  top: -1px;
  left: 11px;
  height: 5px;
  width: 5px;
  background-color: var(--color-error);
  border-radius: 50%;
}

.staff-link,
.green-text {
  cursor: pointer;
  color: var(--main-green);
}

.staff-link:hover,
.green-text:hover {
  color: var(--hover-green);
}

.flex-vertical-align {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  row-gap: 4px;
}

.group-tag {
  margin-bottom: 4px;
}

.no-data-wrapper {
  min-height: 320px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 6px;
  padding: 24px;
  text-align: center;
}

.no-data-text {
  color: var(--main-font);
}

.no-data-wrapper button {
  margin-top: 10px;
}

.no-data-wrapper button:hover {
  border-color: var(--main-green);
  color: var(--main-green);
}

:deep(.ant-table-wrapper .ant-table-thead > tr > th) {
  padding: 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr:not(.ant-table-measure-row) > td) {
  padding: 10px 5px !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.user-type-accord > td) {
  background: #F3FFF0 !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.user-type-accord_range > td) {
  background: #FFFCEC !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.user-type-not_accord > td) {
  background: #FFF6F5 !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected > td) {
  background-color: var(--light-grey) !important;
}

:deep(.ant-table-wrapper .ant-table-tbody > tr.ant-table-row-selected:hover > td) {
  background-color: var(--light-grey) !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger) {
  color: lightgrey;
  height: auto !important;
  padding: 0 !important;
}

:deep(.ant-btn-text.ant-dropdown-trigger.ant-dropdown-open) {
  color: var(--main-font);
}

:deep(.anticon-reload) {
  cursor: pointer;
}

:deep(.anticon-reload:hover) {
  color: var(--main-green) !important;
}

@media (max-width: $mobile-breakpoint) {
  :deep(.ant-pagination .ant-pagination-total-text) {
    display: none !important;
  }
}

.action-button-wrapper {
  display: flex;
  column-gap: 8px;

  @media(max-width: $mobile-breakpoint) {
    display: none;
  }
}

.group-action-wrapper {
  display: none;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 32px;
  background-color: #FFFFFF;
  border-radius: 25%;
  cursor: pointer;

  @media(max-width: $mobile-breakpoint) {
    display: flex;
  }

  :deep(.ant-dropdown-trigger) {
    width: 32px;
    height: 32px !important;
  }
}

.group-action-wrapper {
  border: 1px solid #d9d9d9;

  button {
    color: var(--main-font) !important;
  }
}

:deep(.ant-btn-text.ant-dropdown-trigger) {
  color: lightgrey;
  padding: 0 !important;
}
</style>
